import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useState } from 'react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  Label,
  SimpleTooltip,
  Skeleton,
  Stack,
  Text,
} from '@eluve/components';
import { getLatestTranscriptQuery } from '@eluve/frontend-appointment-hooks';

interface LatestTranscriptMetadataProps {
  appointmentId: string;
  tenantId: string;
}

const LatestTranscriptMetadataContent: React.FC<
  LatestTranscriptMetadataProps
> = ({ appointmentId, tenantId }) => {
  const { data } = useSuspenseQuery(getLatestTranscriptQuery, {
    variables: {
      tenantId: tenantId,
      appointmentId: appointmentId,
    },
  });

  const transcripts = data?.appointmentsByPk?.transcripts;
  const newestTranscript =
    transcripts && transcripts.length > 0
      ? [...transcripts].sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        )[0]
      : null;

  if (!newestTranscript) {
    return (
      <CollapsibleContent>
        <Stack
          gap={3}
          className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-sm text-text-warning-subtle"
          direction="horizontal"
        >
          <Icon name="TriangleAlert" size="lg" />
          No batch transcriptions exist for this appointment.
        </Stack>
      </CollapsibleContent>
    );
  }

  return (
    <CollapsibleContent>
      <Stack
        className="rounded-lg border border-border-quaternary text-sm text-text-secondary"
        direction="horizontal"
      >
        <Stack
          gap={2}
          className="flex-1 p-2"
          direction="horizontal"
          align="center"
        >
          <SimpleTooltip label="Prompt Template">
            <Icon name="FileCode" size="xl" className="mx-1" />
          </SimpleTooltip>
          {newestTranscript.transcriptionSource}
        </Stack>
      </Stack>
    </CollapsibleContent>
  );
};

export const LatestTranscriptMetadata: React.FC<
  LatestTranscriptMetadataProps
> = ({ appointmentId, tenantId }) => {
  const [opened, setOpened] = useState(false);

  return (
    <Collapsible open={opened} onOpenChange={setOpened}>
      <Stack className="space-y-2" gap={2}>
        <Stack gap={2} direction="horizontal">
          <Label className="px-1">Transcript Metadata</Label>
          <CollapsibleTrigger>
            <Stack gap={2} direction="horizontal">
              <Icon
                name="ChevronDown"
                size="lg"
                className='transition-transform duration-300 ease-in-out data-[state="open"]:-rotate-180'
                data-state={opened ? 'open' : 'closed'}
              />
              <Text inline>{!opened ? 'Show' : 'Hide'}</Text>
            </Stack>
          </CollapsibleTrigger>
        </Stack>
        <Suspense
          fallback={
            <CollapsibleContent>
              <Stack
                className="rounded-lg border border-border-quaternary p-4"
                gap={2}
                direction="horizontal"
              >
                <Skeleton className="h-5 w-full" />
              </Stack>
            </CollapsibleContent>
          }
        >
          <LatestTranscriptMetadataContent
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
        </Suspense>
      </Stack>
    </Collapsible>
  );
};
